// Branding context — fetches /api/settings/branding once and applies CSS variables
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';

const BrandingContext = createContext({
  branding: null,
  reload: () => {},
  applyLocal: () => {},
});

export const PRESETS = {
  brasileiro: {
    label: 'Brasileiro (Cantinho)',
    color_primary: '32 78% 52%',     // amber/dendê
    color_secondary: '150 28% 32%',  // forest green muted (mata)
    color_accent: '16 68% 50%',      // terracotta feijoada
    color_background: '36 35% 96%',  // farofa cream
  },
  espanol: {
    label: 'Español (Tapas)',
    color_primary: '14 72% 56%',     // terracotta
    color_secondary: '176 26% 33%',  // teal
    color_accent: '24 55% 90%',
    color_background: '32 45% 97%',
  },
  minimal: {
    label: 'Minimal Pro',
    color_primary: '210 24% 22%',
    color_secondary: '210 14% 50%',
    color_accent: '210 18% 95%',
    color_background: '210 10% 98%',
  },
  vino: {
    label: 'Vino & Madeira',
    color_primary: '352 60% 38%',     // wine
    color_secondary: '28 35% 30%',    // dark wood
    color_accent: '38 50% 90%',
    color_background: '34 25% 95%',
  },
  oceano: {
    label: 'Oceano (Marisquería)',
    color_primary: '199 65% 38%',
    color_secondary: '184 40% 28%',
    color_accent: '195 50% 92%',
    color_background: '195 30% 96%',
  },
};

function applyTokens({ color_primary, color_secondary, color_accent, color_background }) {
  const root = document.documentElement;
  if (color_primary) root.style.setProperty('--primary', color_primary);
  if (color_secondary) root.style.setProperty('--secondary', color_secondary);
  if (color_accent) root.style.setProperty('--accent', color_accent);
  if (color_background) root.style.setProperty('--background', color_background);
  // Derived
  if (color_primary) root.style.setProperty('--ring', color_secondary || color_primary);
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/settings/branding');
      setBranding(r.data);
      applyTokens(r.data);
      if (r.data.nombre) document.title = r.data.nombre;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('branding load error', e);
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyLocal = (overrides) => {
    const merged = { ...branding, ...overrides };
    setBranding(merged);
    applyTokens(merged);
  };

  return (
    <BrandingContext.Provider value={{ branding, reload: load, applyLocal }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

// HSL string ↔ HEX helpers (for the color picker)
export function hslStringToHex(hslStr) {
  if (!hslStr) return '#000000';
  const m = hslStr.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!m) return '#000000';
  const h = Number(m[1]) / 360;
  const s = Number(m[2]) / 100;
  const l = Number(m[3]) / 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToHslString(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let H, S, L = (max + min) / 2;
  if (max === min) { H = S = 0; }
  else {
    const d = max - min;
    S = L > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: H = (g - b) / d + (g < b ? 6 : 0); break;
      case g: H = (b - r) / d + 2; break;
      default: H = (r - g) / d + 4;
    }
    H /= 6;
  }
  return `${Math.round(H * 360)} ${Math.round(S * 100)}% ${Math.round(L * 100)}%`;
}
